import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, MapPin } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Servizi" },
  { to: "/prima-e-dopo", label: "Prima e dopo" },
  { to: "/prezzi", label: "Prezzi" },
  { to: "/studio", label: "Il nostro staff" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-ivory/85 backdrop-blur-xl border-b border-line" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="navbar-logo-link" aria-label="Dentalchivasso — Home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`navbar-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-[0.83rem] font-semibold tracking-wide transition-colors hover:text-teal ${
                    isActive ? "text-teal" : "text-ink/70"
                  } after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-teal after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="tel:01119667070"
              data-testid="navbar-phone-link"
              className="hidden md:inline-flex items-center gap-2 text-[0.83rem] font-semibold text-ink/80 hover:text-teal transition-colors"
            >
              <Phone size={15} className="text-teal" />
              011 19667070
            </a>
            <Link
              to="/contatti"
              data-testid="navbar-cta-button"
              className="hidden sm:inline-flex items-center rounded-full bg-ink text-ivory text-[0.83rem] font-semibold px-5 py-2.5 hover:bg-teal transition-colors duration-300"
            >
              Prenota una visita
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="navbar-menu-open-button"
              aria-label="Apri menu"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line bg-paper/70"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-ivory flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-[72px] px-5 flex items-center justify-between border-b border-line">
              <Logo />
              <button
                onClick={() => setOpen(false)}
                data-testid="navbar-menu-close-button"
                aria-label="Chiudi menu"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-line"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-menu-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `font-display text-4xl font-medium tracking-tight ${isActive ? "text-teal" : "text-ink"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10 space-y-2 text-sm text-mutedc">
              <p className="flex items-center gap-2">
                <MapPin size={15} className="text-teal" /> Via Gerbido 15/3, Chivasso (TO)
              </p>
              <a href="tel:01119667070" data-testid="mobile-menu-phone-link" className="flex items-center gap-2 font-semibold text-ink">
                <Phone size={15} className="text-teal" /> 011 19667070
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}